/**
 * DocumentTypeChangeBadge — klikalna plakietka "(KOD) ✎" w nagłówku formularza JUŻ
 * ZAPISANEGO dokumentu (D.8 krok 2 — dopełnienie `DocumentTypeCreateBadge`, który obsługuje
 * tylko tworzenie). Otwiera `DocumentTypePickerModal` z nadpisaną etykietą przycisku
 * ("Zmień typ", nie "Otwórz formularz" — to nie jest tworzenie nowego dokumentu), a przed
 * wywołaniem `onConfirm` (które ustawia typ w stanie formularza — zapis idzie dopiero
 * głównym PATCH-em) pokazuje `ConfirmDialog` — zmiana typu wydaje nowy numer, więc nie
 * jest to trywialnie odwracalne w UI.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { type DocumentClass, useDocumentTypes } from '@/lib/use-document-types';

interface Props {
  docClass: DocumentClass;
  /** Aktualny typ dokumentu (z rekordu). */
  documentTypeId: string | null;
  /** Firma dokumentu (z rekordu). */
  companyId: string | null;
  /** Wywołane PO potwierdzeniu w ConfirmDialog — caller ustawia typ w stanie formularza. */
  onConfirm: (documentTypeId: string) => void;
  testId: string;
  /** Gating uprawnienia/trybu (poza edycją, brak prawa) — brak = plakietka nieklikalna. */
  disabled?: boolean;
  /** Tooltip nadpisujący domyślny, gdy `disabled` wynika z konkretnego powodu (np. blokada KSeF/CRU). */
  disabledReason?: string;
}

export function DocumentTypeChangeBadge({
  docClass,
  documentTypeId,
  companyId,
  onConfirm,
  testId,
  disabled = false,
  disabledReason,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const { data: types } = useDocumentTypes(docClass, { companyId });
  const selected = types.find((dt) => dt.id === documentTypeId);
  const tooltip = disabled
    ? (disabledReason ?? t('tenant.documents.typePicker.changeTypeDisabledHint'))
    : t('tenant.documents.typePicker.changeTypeAria');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-border-primary bg-bg-secondary px-2 py-0.5 align-middle text-base font-normal text-accent-600 hover:border-accent-500 hover:bg-accent-500/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-primary disabled:hover:bg-bg-secondary"
        data-testid={testId}
        aria-label={tooltip}
        title={tooltip}
      >
        ({selected?.code ?? '…'})
        <Pencil className="h-3 w-3" aria-hidden="true" />
      </button>
      <DocumentTypePickerModal
        open={open}
        docClass={docClass}
        initialCompanyId={companyId}
        initialDocumentTypeId={documentTypeId}
        confirmLabel={t('tenant.documents.typePicker.confirmChange')}
        onSelect={(newTypeId) => {
          setOpen(false);
          if (newTypeId === documentTypeId) return;
          setPending(newTypeId);
        }}
        onClose={() => setOpen(false)}
      />
      <ConfirmDialog
        open={pending !== null}
        variant="primary"
        title={t('tenant.documents.typePicker.confirmChangeTitle')}
        message={t('tenant.documents.typePicker.confirmChangeMessage')}
        confirmLabel={t('tenant.documents.typePicker.confirmChangeAction')}
        onConfirm={() => {
          if (pending) onConfirm(pending);
          setPending(null);
        }}
        onClose={() => setPending(null)}
      />
    </>
  );
}
